import { supabase } from "../../config/supabase";
import { TABLES } from "../../data/tables";
import { ListingStatus } from "../../data/types/domain";

interface GetBusinessListingsParams {
  page?: number;
  pageSize?: number;
  ignoreInactive?: boolean;
}

interface FilterBusinessListingsParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  ignoreInactive?: boolean;
  searchQuery?: string;
  fromLocation?: { name: string; country?: string } | null;
  toLocation?: { name: string; country?: string } | null;
}

export const businessListingQueries = {

  // TODO: Remove this from BusinessListPage and use filterBusinessListings instead
  getBusinessListings: async ({
    page = 1,
    pageSize = 10,
    ignoreInactive = true,
  }: GetBusinessListingsParams = {}) => {
    const offset = (page - 1) * pageSize;

    let query = supabase
      .from(TABLES.BUSINESS)
      .select(`
                *,
                listing_policy:${TABLES.LISTING_POLICY}(*),
                shipping_policy:${TABLES.SHIPPING_POLICY}(*),
                service_location:${TABLES.SERVICE_LOCATION}(*)
            `, { count: 'exact' })
      .not('listing_policy', 'is', null)
      .order('trustpilot_rating', { nullsFirst: false, ascending: false })
      .order('trustpilot_reviews_count', { nullsFirst: false, ascending: false })
      .order('google_rating', { nullsFirst: true, ascending: false })
      .order('google_reviews_count', { nullsFirst: true, ascending: false })
      .range(offset, offset + pageSize - 1);

    // Apply the active status filter if ignoreInactive is true
    if (ignoreInactive) {
      query = query.eq('listing_policy.listing_status', ListingStatus.ACTIVE);
    }

    const { data, error, count } = await query;

    if (error) throw error;

    const mappedData = (data || []).map(business => ({
      business: {
        id: business.id,
        name: business.name,
        description: business.description,
        contact: business.contact,
        email: business.email,
        address: business.address,
        postcode: business.postcode,
        owner: business.owner,
        user_owner: business.user_owner,
        logo: business.logo,
        website: business.website,
        facebook: business.facebook,
        instagram: business.instagram,
        twitter: business.twitter,
        youtube: business.youtube,
        google_rating: business.google_rating,
        google_reviews_count: business.google_reviews_count,
        google_reviews_url: business.google_reviews_url,
        trustpilot_rating: business.trustpilot_rating,
        trustpilot_reviews_count: business.trustpilot_reviews_count,
        trustpilot_reviews_url: business.trustpilot_reviews_url,
        created_at: business.created_at,
        updated_at: business.updated_at,
      },
      listingPolicy: business.listing_policy?.[0],
      shippingPolicies: business.shipping_policy || [],
      serviceLocations: business.service_location || [],
    }));

    return {
      data: mappedData,
      totalCount: count || 0,
      currentPage: page,
      pageSize,
      totalPages: count ? Math.ceil(count / pageSize) : 0
    };
  },

  filterBusinessListings: async ({
    page = 1,
    pageSize = 10,
    sortBy = 'rating',
    sortOrder = 'asc',
    ignoreInactive = true,
    searchQuery = '',
    fromLocation = null,
    toLocation = null,
  }: FilterBusinessListingsParams = {}) => {
    const offset = (page - 1) * pageSize;
    
    let query = supabase
      .from(TABLES.BUSINESS)
      .select(`
                *,
                listing_policy:${TABLES.LISTING_POLICY}(*),
                shipping_policies!inner(
                    *,
                    from_country:countries!shipping_policies_from_country_fkey(id, name),
                    to_country:countries!shipping_policies_to_country_fkey(id, name)
                ),
                service_location:${TABLES.SERVICE_LOCATION}(
                    *,
                    country:countries(*),
                    city:cities(*)
                )
            `, { count: 'exact' })
      .not('listing_policy', 'is', null)
      .order('trustpilot_rating', { nullsFirst: false, ascending: false })
      .order('trustpilot_reviews_count', { nullsFirst: false, ascending: false })
      .order('google_rating', { nullsFirst: true, ascending: false })
      .order('google_reviews_count', { nullsFirst: true, ascending: false });

    // Apply the active status filter if ignoreInactive is true
    if (ignoreInactive) {
      query = query.eq('listing_policy.listing_status', ListingStatus.ACTIVE);
    }

    // Apply search filter
    if (searchQuery) {
      query = query.or(`name.ilike.%${searchQuery}%`);
    }

    // Apply location filter
    // TODO: This needs to be overhauled when we migrate the countries/cities data to the database
    if (toLocation?.name && fromLocation?.name) {
      const toCountryName = toLocation.country || toLocation.name;

      // First get the destination country ID
      const { data: toCountry } = await supabase
        .from('countries')
        .select('id')
        .eq('name', toCountryName)
        .single() as { data: { id: string } | null };


      const fromCountryName = fromLocation.country || fromLocation.name;

      // Get the origin country ID
      const { data: fromCountry } = await supabase
        .from('countries')
        .select('id')
        .eq('name', fromCountryName)
        .single() as { data: { id: string } | null };

      if (!fromCountry || !toCountry) {
        return {
          data: [],
          totalCount: 0,
          currentPage: page,
          pageSize,
          totalPages: 0
        };
      }

      query = query
        .eq('shipping_policies.from_country', fromCountry.id)
        .eq('shipping_policies.to_country', toCountry.id);
    }

    // Apply sorting
    if (sortBy === 'name') {
      query = query.order('name', { ascending: sortOrder === 'asc' });
    }

    // Apply pagination
    query = query.range(offset, offset + pageSize - 1);

    const { data, error, count } = await query;

    if (error) throw error;

    const mappedData = (data || []).map(business => ({
      business: {
        id: business.id,
        name: business.name,
        description: business.description,
        contact: business.contact,
        email: business.email,
        address: business.address,
        postcode: business.postcode,
        owner: business.owner,
        user_owner: business.user_owner,
        logo: business.logo,
        website: business.website,
        facebook: business.facebook,
        instagram: business.instagram,
        twitter: business.twitter,
        youtube: business.youtube,
        google_rating: business.google_rating,
        google_reviews_count: business.google_reviews_count,
        google_reviews_url: business.google_reviews_url,
        trustpilot_rating: business.trustpilot_rating,
        trustpilot_reviews_count: business.trustpilot_reviews_count,
        trustpilot_reviews_url: business.trustpilot_reviews_url,
        created_at: business.created_at,
        updated_at: business.updated_at,
      },
      listingPolicy: business.listing_policy?.[0],
      // TODO: Find a better way to convert the shipping policies to the correct type
      shippingPolicies: (business.shipping_policies || []).map((policy: any) => ({
        id: policy.id,
        from_country: policy.from_country.id,
        to_country: policy.to_country.id,
        minimum_order_amount: policy.minimum_order_amount,
        minimum_per_kg_price: policy.minimum_per_kg_price,
        minimum_order_type: policy.minimum_order_type,
        created_at: policy.created_at,
        updated_at: policy.updated_at
      })),
      serviceLocations: business.service_location || [],
    }));

    console.log(mappedData);

    return {
      data: mappedData,
      totalCount: count || 0,
      currentPage: page,
      pageSize,
      totalPages: count ? Math.ceil(count / pageSize) : 0
    };
  }
};

